<template>
  <div>
    <execution-level-legend
      :legend-data="executionLevelArchieved"
      title="Leyenda de Nivel de Ejecución Alcanzado"
    />

    <div
      v-if="goals.length == 0 && !isAddGoalMode"
      class="bg-secondary-13 p-3 rounded text-center"
    >
      <action-button
        class="border btn px-5"
        i="fas fa-file-plus color-green"
        text="Presione aquí para añadir metas"
        @click="addItem"
      />
    </div>

    <template v-if="goals.length && !isAddGoalMode">
      <div v-for="(goal, idx) in goalsSorted" :key="`goal-${idx}`">
        <div
          class="row mx-0 aling-items-center bg-secondary-3 rounded py-3 px-4 mb-3"
        >
          <div class="col-12 col-md-10 d-flex flex-column">
            <div class="d-flex">
              <span
                class="badge badge-pill bg-secondary-2 px-3 d-flex align-items-center font-16 font-weight-bold color-white"
              >
                Meta #{{ idx + 1 }}
              </span>
              <span
                v-if="goal.academicYear"
                class="badge badge-pill px-3 d-flex align-items-center font-16 font-weight-bold ml-3 color-black"
                :style="{
                  'background-color':
                    goal.executionLevelArchieved?.color || '#ccc',
                }"
              >
                {{
                  `${goal.academicYear?.description ?? ''} - ${goal
                    .executionLevelArchieved?.name ?? ''}`
                }}
              </span>
            </div>
            <div class="d-flex align-items-center flex-md-row flex-column mt-2">
              <div class="font-weight-normal color-black flex-grow-1">
                {{ computedDescription(goal) }}
              </div>
            </div>
          </div>
          <div
            v-if="!disabled"
            class="col-12 col-md-2 d-flex justify-content-end align-items-center"
          >
            <dropdown
              class="actions-dropdown"
              :items="actionItems(goal, idx)"
              title="Acciones"
            />
          </div>
        </div>
      </div>
      <div v-if="!disabled" class="d-flex justify-content-end">
        <action-button
          i="fas fa-file-plus"
          text="Añadir"
          variant="success mt-2"
          @click="addItem"
        />
      </div>
    </template>
    <template v-else-if="isAddGoalMode">
      <custom-collapse
        id="meta-collapse"
        class="intervention-plan-collapse mt-4 mx-md-3"
      >
        <custom-collapse-item
          id="collapse-item-academic-year"
          :ref="accordionItems.academicYear.name"
          body-background-color="#F7F7F7"
          :checked="accordionItems.academicYear.checked"
          class="border-bottom"
          :disabled="accordionItems.academicYear.disabled"
          :expanded="accordionItems.academicYear.expanded"
          parent-id="meta-collapse"
          :selected="!accordionItems.academicYear.disabled"
          title="Año Académico"
        >
          <validation-observer ref="academicYearForm">
            <div class="row d-flex">
              <div class="col-12 col-md-4">
                <validation-provider v-slot="{ errors }" rules="required">
                  <custom-drop-down
                    v-model="model.academicYear"
                    class="pl-md-4"
                    custom-error-msg="El año escolar es requerido."
                    :error="errors[0]"
                    fieldkey="id"
                    fieldname="description"
                    label="Año Académico:"
                    :options="filteredAcademicYears"
                    placeholder="Seleccione"
                  />
                </validation-provider>
              </div>
              <div
                class="col-12 col-md flex-grow-1 d-flex align-items-center justify-content-end pr-md-4 pt-3"
              >
                <action-button
                  class="px-3 mr-2"
                  i="fas fa-times-circle"
                  text="Cancelar"
                  variant="outline-danger"
                  @click="cancelEdit"
                />
                <action-button
                  class="px-3"
                  i="fas fa-arrow-alt-circle-right"
                  text="Continuar"
                  variant="success"
                  @click="academicYearNextHandler()"
                />
              </div>
            </div>
          </validation-observer>
        </custom-collapse-item>
        <custom-collapse-item
          id="collapse-item-goal"
          :ref="accordionItems.goal.name"
          body-background-color="#F7F7F7"
          :checked="accordionItems.goal.checked"
          class="border-bottom"
          :disabled="accordionItems.goal.disabled"
          :expanded="accordionItems.goal.expanded"
          parent-id="meta-collapse"
          :selected="!accordionItems.goal.disabled"
          title="Metas"
        >
          <multi-selection-picker
            add-item-title-label="Añadir nueva Estrategia a su listado"
            :all-items="catalog.goals"
            allow-item-list-editing
            :column-count="1"
            edit-item-title-label="Editar Estrategia de su listado"
            id-prop-name="id"
            item-type-name="meta"
            maxlength="300"
            modify-removes-item
            placeholder="Escriba aquí para buscar una"
            :selected-items="catalog.addedGoals"
            text-prop-name="name"
            @itemAdded="onAddedGoalHandler"
            @itemModified="onModifiedGoalHandler"
            @itemSelected="onSelectedGoalHandler"
          />
          <custom-chips-group
            v-if="catalog.addedGoals.length"
            no-background
            no-wrap
            object-text="name"
            :on-item-deleted="onDeleteGoalHandler"
            :selected-items="catalog.addedGoals"
            title="Metas seleccionadas"
          />
          <custom-error
            is-alert-mode
            message="No se puede seleccionar más de una meta."
            variant="mt-2 mb-0"
          />
          <div
            class="col flex-grow-1 d-flex align-items-center justify-content-end pt-3"
          >
            <action-button
              class="px-3 mr-2"
              i="fas fa-times-circle"
              text="Cancelar"
              variant="outline-danger"
              @click="cancelEdit"
            />
            <action-button
              class="px-3"
              i="fas fa-arrow-alt-circle-right"
              text="Continuar"
              variant="success"
              @click="goalNextHandler"
            />
          </div>
        </custom-collapse-item>
        <custom-collapse-item
          id="collapse-item-execution-level"
          :ref="accordionItems.levelAchieved.name"
          body-background-color="#F7F7F7"
          :checked="accordionItems.levelAchieved.checked"
          class="collapse-item"
          :disabled="accordionItems.levelAchieved.disabled"
          :expanded="accordionItems.levelAchieved.expanded"
          parent-id="meta-collapse"
          :selected="!accordionItems.levelAchieved.disabled"
          title="Nivel de Ejecución Alcanzado"
        >
          <validation-observer ref="levelAchievedForm">
            <validation-provider
              v-slot="{ errors }"
              :custom-messages="{
                required: 'El nivel de ejecución es requerido',
              }"
              rules="required"
            >
              <execution-level-selector
                v-model="model.executionLevelArchieved"
                :error="errors[0]"
                :execution-level-archieved="executionLevelArchieved"
              />
            </validation-provider>
            <div class="row mt-3 d-flex ml-n1">
              <div class="col-12 col-md-3">
                <validation-provider
                  v-if="isExecutionLeveRequired"
                  v-slot="{ errors }"
                  :custom-messages="{
                    required: 'El porciento es requerido.',
                    max_value: 'El valor maximo es 100.',
                  }"
                  :rules="
                    `differentFromZero|max_value:100|${
                      isExecutionLeveRequired ? 'required|greaterThanZero' : ''
                    }`
                  "
                >
                  <custom-input
                    v-model="model.executionLevelPercent"
                    :error="errors[0]"
                    label="Porciento:"
                    type="number"
                  />
                </validation-provider>
              </div>
              <div
                class="col flex-grow-1 d-flex align-items-center justify-content-end pt-3"
              >
                <action-button
                  class="px-3 mr-2"
                  i="fas fa-times-circle"
                  text="Cancelar"
                  variant="outline-danger"
                  @click="cancelEdit"
                />
                <action-button
                  class="px-3"
                  i="fas fa-arrow-alt-circle-right"
                  text="Continuar"
                  variant="success"
                  @click="levelNextHandler"
                />
              </div>
            </div>
          </validation-observer>
        </custom-collapse-item>
      </custom-collapse>
    </template>
  </div>
</template>

<script>
import { CustomCollapse, CustomCollapseItem } from 'custom-collapse';
import ExecutionLevelLegend from '@/views/provider/referral-management/search/components/ExecutionLevelLegend.vue';
import ActionButton from 'ActionButton';
import Dropdown from 'dropdown';
import MultiSelectionPicker from '@/components/common/MultiSelectionPicker';
import CustomDropDown from 'custom-drop-down';
import CustomChipsGroup from 'CustomChipsGroup';
import CustomInput from 'CustomInput';
import ExecutionLevelSelector from '@/views/provider/referral-management/search/components/ExecutionLevelSelector';
import CustomError from 'custom-error';
import _ from 'lodash';
import { getSysparameterByNames } from '@/modules/shared/sysparameter.api';
import sysparameterByNames from '@/utils/constants/sysParameterNames';

const MAX_GOAL_SELECTION = 1;
const executionLevelNA = 6;

export default {
  name: 'LongTermGoals',
  components: {
    CustomCollapse,
    CustomCollapseItem,
    ExecutionLevelLegend,
    ActionButton,
    Dropdown,
    MultiSelectionPicker,
    CustomDropDown,
    CustomChipsGroup,
    CustomInput,
    ExecutionLevelSelector,
    CustomError,
  },
  props: {
    executionLevelArchieved: {
      type: Array,
      default: () => [],
    },
    academicYears: {
      type: Array,
      default: () => [],
    },
    goals: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: () => false,
    },
  },
  data: () => ({
    idCounter: 0,
    isAddGoalMode: false,
    model: {
      goal: null,
      academicYear: null,
      executionLevelArchieved: null,
      executionLevelPercent: null,
    },
    catalog: {
      goals: [],
      addedGoals: [],
    },
    accordionItems: {
      academicYear: {
        name: 'academicYear',
        checked: false,
        expanded: true,
        disabled: false,
      },
      goal: {
        name: 'goal',
        checked: false,
        expanded: false,
        disabled: true,
      },
      levelAchieved: {
        name: 'levelAchieved',
        checked: false,
        expanded: false,
        disabled: true,
      },
      parameterValues: [],
    },
  }),
  computed: {
    filteredAcademicYears() {
      const currentYear = new Date().getFullYear();

      return this.academicYears.filter(
        (academicYear) =>
          new Date(academicYear.fromDate).getFullYear() >= currentYear - 5
      );
    },
    computedDescription() {
      return (item) =>
        `${item.selectedGoal.name}. En el ${
          !_.isNil(item.executionLevelPercent) ? item.executionLevelPercent : 0
        } % de las veces.`;
    },
    isExecutionLeveRequired() {
      const levelSelected = this.model.executionLevelArchieved?.id;
      return levelSelected !== executionLevelNA; // N/A selected
    },
    actionItems() {
      return (goal, index) => [
        {
          name: 'edit',
          label: 'Documentar Nivel de Ejecución',
          icon: 'fas fa-edit text-green pr-1',
          visible: true,
          click: () => this.editGoal(goal, index),
        },
        {
          name: 'delete',
          label: 'Eliminar',
          icon: 'fas fa-times-circle color-red pr-1',
          visible: true,
          click: () => this.deleteGoal(goal, index),
        },
      ];
    },
    goalsSorted() {
      return _.sortBy(this.goals, (item) => item.academicYear.id).reverse();
    },
    isNewFeaturesImprovementEnabled() {
      const paramValue = this.parameterValues.find(
        (x) =>
          x.name == sysparameterByNames.ResultSummaryFeatureImprovementEnabled
      );

      return !paramValue
        ? false
        : paramValue.staticValue.toUpperCase() == 'TRUE';
    },
  },
  async created() {
    await this.fetchParameters();
  },
  methods: {
    onSelectedGoalHandler(item) {
      if (this.catalog.addedGoals.length > MAX_GOAL_SELECTION) {
        this.catalog.addedGoals = this.catalog.addedGoals.filter(
          (g) => g.id !== item.id
        );
      }
    },
    onAddedGoalHandler(itemText) {
      this.catalog.goals.push({
        id: (this.idCounter += -1),
        name: itemText,
      });
    },
    onModifiedGoalHandler(item, index) {
      if (item.id > 0) {
        item.isDirty = true;
      }
      this.catalog.goals.splice(index, 0, item);
    },
    onDeleteGoalHandler(item) {
      this.catalog.addedGoals.splice(this.catalog.addedGoals.indexOf(item), 1);
    },
    cancelEdit() {
      this.model = {};
      this.isAddGoalMode = false;
      this.catalog.addedGoals = [];
    },
    addItem() {
      this.openAccordionSection(this.accordionItems.academicYear.name);
      this.isAddGoalMode = true;
    },
    openAccordionSection(toOpen, toCheck, enableAll = false) {
      Object.keys(this.accordionItems).forEach((prop) => {
        if (prop === toCheck || (toCheck == null && enableAll))
          this.accordionItems[prop].checked = true;
        else if (toCheck == null) this.accordionItems[prop].checked = false;

        const openSection = Boolean(prop === toOpen);
        const section = this.$refs[prop];
        this.accordionItems[prop].expanded = openSection;
        this.accordionItems[prop].disabled = enableAll ? false : !openSection;
        openSection ? section?.expandItem() : section?.collapseItem();
      });
    },
    editGoal(goal, index) {
      this.model = { ...goal, index };
      this.catalog.addedGoals.push(goal.selectedGoal);
      this.openAccordionSection(
        this.accordionItems.academicYear.name,
        null,
        true
      );

      this.isAddGoalMode = true;
    },
    deleteGoal(_, index) {
      //El index the las metas no es el mismo de las metas ordenadas
      const mainIndex = this.goals.indexOf(this.goalsSorted[index]);
      this.goals.splice(mainIndex, 1);
    },
    async academicYearNextHandler() {
      const isValid = await this.$refs.academicYearForm.validate();
      if (!isValid) {
        this.$emit('alert', 'Información Incompleta');
        return;
      }
      this.openAccordionSection(
        this.accordionItems.goal.name,
        this.accordionItems.academicYear.name
      );
    },
    async goalNextHandler() {
      if (this.catalog.addedGoals.length == 0) {
        this.$emit('alert', 'Favor seleccione una meta');
        return;
      }
      this.model.selectedGoal = this.catalog.addedGoals[0];
      this.openAccordionSection(
        this.accordionItems.levelAchieved.name,
        this.accordionItems.goal.name
      );
    },
    async levelNextHandler() {
      const isValid = await this.$refs.levelAchievedForm.validate();
      if (!isValid) {
        this.$emit('alert', 'Información Incompleta');
        return;
      }

      if (this.model.index != undefined) {
        //El index the las metas no es el mismo de las metas ordenadas
        const mainIndex = this.goals.indexOf(
          this.goalsSorted[this.model.index]
        );
        this.goals.splice(mainIndex, 1, this.model);
      } else this.goals.push(this.model);

      this.cancelEdit();
    },
    async fetchParameters() {
      const { data } = await getSysparameterByNames({
        parameterNames: [
          sysparameterByNames.ResultSummaryFeatureImprovementEnabled,
        ],
      });

      this.parameterValues = [...data];
    },
  },
};
</script>

<style lang="scss" scoped></style>
